import type * as api from '@gitbook/api-client';
import {
    createPalette,
    PaletteButton,
    PaletteCommandType,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';

import { apiResource, readAPICache, useAssertCurrentOrganizationId } from '../../state';
import { UserAvatar } from '../Users';

type MergeRuleWithUsers = Extract<
    api.MergeRule,
    { rule: 'require_specific_reviewers' | 'require_one_of_specific_reviewers' | 'allow_bypass' }
>;

/**
 * Let the user picks users for the rule.
 */
export function MergeRuleUsersPicker(props: {
    mergeRule: MergeRuleWithUsers;
    onChange: (mergeRule: MergeRuleWithUsers) => void;
}) {
    const { mergeRule, onChange } = props;
    const organizationId = useAssertCurrentOrganizationId();

    return (
        <div className="flex flex-row gap-3">
            <PaletteButton
                label={`${mergeRule.users.length} selected`}
                kind="secondary"
                trailingIcon={Icon.ChevronDown}
                palette={() =>
                    createPalette({
                        search: { placeholder: 'Search users' },
                        position: {
                            width: PalettePositionWidth.LARGE,
                        },
                        groups: [
                            {
                                key: 'members',
                                commands: (palette) => {
                                    const response = readAPICache(
                                        apiResource(
                                            'listMembersInOrganizationById',
                                            {
                                                organizationId,
                                                search: palette.query.text,
                                                limit: 8,
                                            },
                                            { nullOnError: true }
                                        )
                                    );
                                    if (!response) {
                                        return [];
                                    }

                                    return response.items.map((member) => {
                                        const selected = mergeRule.users.includes(member.user.id);
                                        return {
                                            key: member.user.id,
                                            type: PaletteCommandType.CHECKBOX,
                                            selected,
                                            icon: () => (
                                                <UserAvatar
                                                    user={member.user}
                                                    size="md"
                                                    tooltip={false}
                                                />
                                            ),
                                            title: member.user.displayName,
                                            subtitle: member.user.email,
                                            trigger: () => {
                                                onChange({
                                                    ...mergeRule,
                                                    users: [
                                                        ...mergeRule.users.filter(
                                                            (id) => id !== member.user.id
                                                        ),
                                                        ...(selected ? [] : [member.user.id]),
                                                    ],
                                                });
                                            },
                                        };
                                    });
                                },
                            },
                        ],
                    })
                }
            />
        </div>
    );
}
